import { ReactNode } from "react";
import { Box } from "../Box/Box";
import { ResponsiveProp } from "../utils/responsiveProp";
import styles from "./Text.module.css";

export type TextSize = "xsmall" | "small" | "standard" | "large" | "xlarge";
export interface Props {
  size?: TextSize;
  weight?: "regular" | "medium" | "semi-strong" | "strong";
  transform?: "uppercase" | "capitalize";
  align?: ResponsiveProp<"left" | "right" | "center">;
  truncate?: boolean;
  clamped?: "1" | "2" | "3" | "4" | "5";
  /** Text color - note "primary" is not always safe to use over a white background for text */
  color?: Color;
  lineThrough?: boolean;
  children: ReactNode;
  /** If a font tag is not provided it will default to inherit the font-family */
  font?: "accent" | "regular" | "special";
  /** Truncate priority if set to low it will attempt to truncate first*/
  priority?: "low";
  lineHeight?: "standard" | "large";
  whiteSpace?: "pre-wrap" | "pre-line";
  wordWrap?: "break-word";
  inline?: boolean;
}

export function Text(props: Props): React.ReactElement {
  const classes: string[] = [];
  classes.push(styles[`textSize-${props.size || "standard"}`]);
  classes.push(styles[`textWeight-${props.weight || "regular"}`]);
  if (props.transform) {
    classes.push(styles[`transform-${props.transform}`]);
  }
  if (props.truncate) {
    classes.push(styles.truncate);
  }
  if (props.clamped) {
    classes.push(styles.clamped);
    classes.push(styles[`lines-${props.clamped}`]);
  }
  if (props.color) {
    classes.push(styles[`color-${props.color}`]);
  }
  if (props.lineThrough) {
    classes.push(styles.lineThrough);
  }

  if (props.font) {
    classes.push(styles[`font-${props.font}`]);
  }

  if (props.priority) {
    classes.push(styles[`priority-${props.priority}`]);
  }

  if (props.lineHeight) {
    classes.push(styles[`lineHeight-${props.lineHeight}`]);
  }
  if (props.whiteSpace) {
    classes.push(styles[`whiteSpace-${props.whiteSpace}`]);
  }
  if (props.wordWrap) {
    classes.push(styles[`wordWrap-${props.wordWrap}`]);
  }

  return (
    <Box
      textAlign={props.align}
      className={classes.join(" ")}
      component={props.inline ? "span" : undefined}
    >
      {props.children}
    </Box>
  );
}

export type Color =
  | "primary"
  | "accent"
  | "overPrimary"
  | "warning"
  | "neutral-100"
  | "neutral-200"
  | "neutral-300"
  | "neutral-400"
  | "neutral-500"
  | "neutral-600"
  | "neutral-700"
  | "neutral-800"
  | "neutral-900"
  | "error"
  | "success"
  | "overImage"
  | "info"
  | "link"
  | "jets-black"
  | "jets-beige"
  | "jets-red";
